/**
 * Settings Tab Component - Supplier-specific configuration and settings
 * Includes the per-site OTIF window, company information editing, supplier config,
 * and customer relationship management.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  Loader2, Info, Layers, UserCog, BarChart3,
} from 'lucide-react';
import { coreAPI, purchasingAPI, fetchAllPages, apiErrorMessage, SupplierProfile, SupplierCustomerRelationship, CommodityGroup, CommodityType, Site, UserProfile as APIUserProfile } from '../../lib/api';
import { toast } from 'sonner';
import { CustomerRelationshipsSection } from './CustomerRelationshipsSection';
import { assignCustomerCode } from './assignCustomerCode';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import { QBRScheduleSection } from './QBRScheduleSection';
import { SupplierConfigurationCard } from './SupplierConfigurationCard';
import { OtifWindowCard } from './OtifWindowCard';
import { fetchCompanyUsersIfAllowed } from './utils';

interface SettingsTabProps {
  supplier: SupplierProfile;
  isHelixAdmin: boolean;
  isCustomer: boolean;
  /** Customer (or helix) admin — may change Strategic / Call-Off (review item 33). */
  isCustomerAdmin?: boolean;
  onUpdateProfile: (data: Partial<SupplierProfile>) => Promise<void>;
}

export function SettingsTab({
  supplier,
  isHelixAdmin, isCustomer, isCustomerAdmin,
  onUpdateProfile,
}: SettingsTabProps) {
  const { isMenuItemVisible } = useMenuSettings();
  const canEditSettings = isHelixAdmin || isCustomer;
  // SITE-KPI-FLAGS: the supplier-wide KPI switches count at every site, so the
  // server only takes them from Helix staff or a customer whose role is an
  // admin role (IsHelixOrCustomerAdmin) — not every role that can manage users.
  const { user } = useAuth();
  const canEditKpiSwitches = isHelixAdmin || (isCustomer && !!user?.is_company_admin);
  // CR-8613: the platform's Call Off switch alone. It used to need
  // switch_call_off_tab as well, which is only true once some supplier already
  // has Call-Off on — so a customer with none could never switch on the first.
  const callOffAvailable = isMenuItemVisible('callOff');
  const qualityLabel = useQualityMeasureLabel();

  // Customer Relationships state
  const [relationships, setRelationships] = useState<SupplierCustomerRelationship[]>([]);
  const [relLoading, setRelLoading] = useState(false);
  const [relSaving, setRelSaving] = useState<number | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [assigning, setAssigning] = useState(false);
  // Optional code to create alongside (or against) the chosen customer.
  const [newCodeValue, setNewCodeValue] = useState('');
  const [newCodeCurrency, setNewCodeCurrency] = useState('');

  // CR-4362: Commodity Settings state
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);
  const [commoditySaving, setCommoditySaving] = useState(false);

  // CR-4363: Management Settings state
  const [supplierUsers, setSupplierUsers] = useState<APIUserProfile[]>([]);
  // Buyer candidates per customer company. A buyer belongs to the customer
  // doing the buying, so each relationship offers its own customer's users.
  const [buyerOptionsByCustomer, setBuyerOptionsByCustomer] = useState<Record<number, APIUserProfile[]>>({});
  const [managementSaving, setManagementSaving] = useState(false);
  // CR-6794: quality engineer is free text, so it is held locally and saved on
  // blur — a PATCH per keystroke would fire a toast per character.
  const [qualityEngineer, setQualityEngineer] = useState(supplier.quality_engineer ?? '');
  useEffect(() => { setQualityEngineer(supplier.quality_engineer ?? ''); }, [supplier.quality_engineer]);
  // CR-7345: the support quality engineer is free text too, saved on blur.
  const [supportQualityEngineer, setSupportQualityEngineer] = useState(supplier.support_quality_engineer ?? '');
  useEffect(() => { setSupportQualityEngineer(supplier.support_quality_engineer ?? ''); }, [supplier.support_quality_engineer]);

  const fetchSupplierUsers = useCallback(async () => {
    try {
      setSupplierUsers(await fetchCompanyUsersIfAllowed(supplier.company));
    } catch (err: unknown) {
      console.error('Error loading supplier users:', err);
    }
  }, [supplier.company]);

  const updateManagement = async (data: Partial<SupplierProfile>) => {
    setManagementSaving(true);
    try {
      await onUpdateProfile(data);
      toast.success('Management settings updated');
    } catch {
      toast.error('Failed to update management settings');
    } finally {
      setManagementSaving(false);
    }
  };

  /**
   * Save the quality engineer only when it actually changed.
   *
   * Tabbing through a field nobody touched must not fire a PATCH and a toast.
   * A failed save leaves the typed text in place — a rejected value is
   * something the user needs to see to correct, not to silently lose.
   */
  const commitQualityEngineer = () => {
    const next = qualityEngineer.trim();
    if (next === (supplier.quality_engineer ?? '')) return;
    updateManagement({ quality_engineer: next } as Partial<SupplierProfile>);
  };

  // CR-7345: mirror commitQualityEngineer for the support quality engineer.
  const commitSupportQualityEngineer = () => {
    const next = supportQualityEngineer.trim();
    if (next === (supplier.support_quality_engineer ?? '')) return;
    updateManagement({ support_quality_engineer: next } as Partial<SupplierProfile>);
  };

  const fetchCommodities = useCallback(async () => {
    try {
      const [gRes, types] = await Promise.all([
        purchasingAPI.getCommodityGroups({ page_size: 100 }),
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        fetchAllPages<CommodityType>((page) => purchasingAPI.getCommodityTypes({ page_size: 100, page })),
      ]);
      const gData = gRes.data;
      setCommodityGroups(Array.isArray(gData) ? gData : (gData as Record<string, unknown>)['results'] as CommodityGroup[]);
      setCommodityTypes(types);
    } catch (err: unknown) {
      console.error('Error loading commodities:', err);
    }
  }, []);

  const updateCommodity = async (data: Partial<SupplierProfile>) => {
    setCommoditySaving(true);
    try {
      await onUpdateProfile(data);
      toast.success('Commodity settings updated');
    } catch {
      toast.error('Failed to update commodity settings');
    } finally {
      setCommoditySaving(false);
    }
  };

  const fetchRelationships = useCallback(async () => {
    setRelLoading(true);
    try {
      const res = await coreAPI.getSupplierRelationships({ supplier: supplier.company, page_size: 100 });
      const data = res.data;
      const rels = (Array.isArray(data) ? data : (data as Record<string, unknown>)['results'] as SupplierCustomerRelationship[]) ?? [];
      setRelationships(rels);
      // Buyer candidates come from each customer's own user list. Fetched once
      // per distinct customer here rather than per row, and failures are
      // swallowed per company so one inaccessible customer cannot blank the
      // picker for the rest.
      const customerIds = Array.from(new Set(rels.map((r) => r.customer)));
      const entries = await Promise.all(customerIds.map(async (id) => {
        try {
          const usersRes = await coreAPI.getCompanyUsers(id);
          return [id, Array.isArray(usersRes.data) ? usersRes.data : []] as const;
        } catch {
          return [id, [] as APIUserProfile[]] as const;
        }
      }));
      setBuyerOptionsByCustomer(Object.fromEntries(entries));
    } catch (err: unknown) {
      console.error('Error loading relationships:', err);
    } finally {
      setRelLoading(false);
    }
  }, [supplier.company]);

  useEffect(() => {
    fetchRelationships();
    fetchCommodities();
    fetchSupplierUsers();
  }, [fetchRelationships, fetchCommodities, fetchSupplierUsers]);

  const updateRelationship = async (id: number, data: Partial<SupplierCustomerRelationship>) => {
    setRelSaving(id);
    try {
      await coreAPI.updateSupplierRelationship(id, data);
      setRelationships((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
      toast.success('Relationship updated');
    } catch (err: unknown) {
      console.error('Error updating relationship:', err);
      toast.error('Failed to update relationship');
    } finally {
      setRelSaving(null);
    }
  };

  const openAssignDialog = async () => {
    try {
      const res = await coreAPI.getAccessibleSites();
      const sites: Site[] = Array.isArray(res.data) ? res.data : (res.data as Record<string, unknown>)['results'] as Site[];
      // CR-7272: an already-assigned site stays in the list. Picking one adds
      // another supplier code to that customer rather than a second
      // relationship, which is how a customer ends up trading with one
      // supplier under both a GBP and a USD code.
      setAvailableSites(sites);
      setSelectedSiteId(null);
      setNewCodeValue('');
      setNewCodeCurrency('');
      setAssignDialogOpen(true);
    } catch (err: unknown) {
      console.error('Error loading sites:', err);
      toast.error('Failed to load sites');
    }
  };

  const assignSite = async () => {
    if (!selectedSiteId) return;
    const code = newCodeValue.trim();
    if (code && !newCodeCurrency) {
      toast.error('Choose the currency this code trades in');
      return;
    }
    const existing = relationships.find((r) => r.customer === selectedSiteId);
    if (existing && !code) {
      toast.error(`Enter the supplier code to add to ${existing.customer_name}`);
      return;
    }
    setAssigning(true);
    try {
      const outcome = await assignCustomerCode({
        supplierCompanyId: supplier.company, customerId: selectedSiteId, existing, code, currency: newCodeCurrency,
      });
      // CR-6794: the primary code is seeded on commit, so it is not in the
      // create response — refetch rather than appending.
      await fetchRelationships();
      setAssignDialogOpen(false);
      toast.success(outcome === 'assigned' ? 'Customer assigned' : `${code} added to ${existing?.customer_name}`);
    } catch (err: unknown) {
      console.error('Error assigning site:', err);
      toast.error(apiErrorMessage(err, 'Failed to assign customer'));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* CR-4819: Company Information moved to Overview tab to remove duplication */}

      {/* Customer Relationships */}
      <CustomerRelationshipsSection
        relationships={relationships} relLoading={relLoading} relSaving={relSaving}
        canEditSettings={canEditSettings}
        assignDialogOpen={assignDialogOpen} setAssignDialogOpen={setAssignDialogOpen}
        availableSites={availableSites} selectedSiteId={selectedSiteId}
        setSelectedSiteId={setSelectedSiteId} assigning={assigning}
        buyerOptionsByCustomer={buyerOptionsByCustomer} updateRelationship={updateRelationship}
        kpiMaster={{
          include_in_otif: supplier.include_in_otif,
          include_in_otd: supplier.include_in_otd,
          include_in_ncr_ppm: supplier.include_in_ncr_ppm,
        }}
        qualityLabel={qualityLabel}
        newCodeValue={newCodeValue} setNewCodeValue={setNewCodeValue}
        newCodeCurrency={newCodeCurrency} setNewCodeCurrency={setNewCodeCurrency}
        assignedCustomerIds={relationships.map((r) => r.customer)}
        setRelationships={setRelationships}
        openAssignDialog={openAssignDialog}
        assignSite={assignSite}
      />

      {/* Supplier Configuration — CR-6354 keeps Strategic / Call-Off on the
          relationship (the field the dashboard "Strategic Suppliers Only"
          filter reads); Customer review 11 Sep (item 33) makes each ONE
          switch per customer organisation, written to all its sites. Call-Off
          stays hidden when the tenant does not have Call Off. */}
      <SupplierConfigurationCard
        supplierCompanyId={supplier.company}
        relationships={relationships}
        relLoading={relLoading}
        canEdit={isHelixAdmin || !!isCustomerAdmin}
        callOffAvailable={callOffAvailable}
        onRelationshipsUpdated={(updated) => {
          const byId = new Map(updated.map((r) => [r.id, r]));
          setRelationships((prev) => prev.map((r) => byId.get(r.id) ?? r));
        }}
      />

      {/* METRIC-DEFINITIONS Q3: the supplier's OTIF window at each customer
          site, following the top-bar view (PERF-SETTINGS-HIGHS mockup 01). */}
      <OtifWindowCard
        relationships={relationships}
        relLoading={relLoading}
        canEdit={isHelixAdmin || !!isCustomerAdmin}
        onRelationshipUpdated={(updated) => setRelationships((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))}
      />

      {/* CR-4362: Commodity Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />Commodity Settings</CardTitle>
          <CardDescription>Assign commodity group and type classification for this supplier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Group</label>
              <select value={supplier.commodity_group ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateCommodity({ commodity_group: val, commodity_type: null } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || commoditySaving}>
                <option value="">None</option>
                {commodityGroups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Type</label>
              <select value={supplier.commodity_type ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateCommodity({ commodity_type: val } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || commoditySaving || !supplier.commodity_group}>
                <option value="">None</option>
                {commodityTypes.filter((t) => t.commodity_group === supplier.commodity_group).map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </select>
            </div>
          </div>
          {commoditySaving && (<div className="flex items-center gap-2 text-sm text-muted-foreground mt-3"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>)}
        </CardContent>
      </Card>

      {/* CR-4363: Management Settings. CR-6794 added Buyer and Quality
          Engineer — both describe who owns this supplier, so they belong here
          beside the commodity manager and SDM rather than on each supplier
          code. Quality Engineer stays free text: it mirrors the legacy
          company-level field the Supplier Quality Master Data import writes,
          and those names are not always Helix users. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UserCog className="h-5 w-5" />Management Settings</CardTitle>
          <CardDescription>Assign the people responsible for this supplier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Manager</label>
              <select value={supplier.commodity_manager ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateManagement({ commodity_manager: val } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || managementSaving}>
                <option value="">Not assigned</option>
                {supplierUsers.map((u) => (<option key={u.user.id} value={u.user.id}>{u.user.full_name || u.user.email}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Supplier Development Manager</label>
              <select value={supplier.supplier_development_manager ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateManagement({ supplier_development_manager: val } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || managementSaving}>
                <option value="">Not assigned</option>
                {supplierUsers.map((u) => (<option key={u.user.id} value={u.user.id}>{u.user.full_name || u.user.email}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" htmlFor="supplier-buyer">Buyer</label>
              <select id="supplier-buyer" value={supplier.buyer ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateManagement({ buyer: val } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || managementSaving}>
                <option value="">Not assigned</option>
                {/* A buyer set by the ERP or an earlier admin may no longer be
                    in the list. Carrying them as their own option stops the
                    field rendering blank and quietly reassigning on save. */}
                {supplier.buyer !== null && !supplierUsers.some((u) => u.user.id === supplier.buyer) && (
                  <option value={supplier.buyer}>{supplier.buyer_name || 'Unknown user'}</option>
                )}
                {supplierUsers.map((u) => (<option key={u.user.id} value={u.user.id}>{u.user.full_name || u.user.email}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" htmlFor="supplier-quality-engineer">Quality Engineer</label>
              <Input
                id="supplier-quality-engineer"
                value={qualityEngineer}
                placeholder="Not assigned"
                disabled={!canEditSettings || managementSaving}
                onChange={(e) => setQualityEngineer(e.target.value)}
                onBlur={() => { if (canEditSettings) commitQualityEngineer(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" htmlFor="supplier-support-quality-engineer">Support Quality Engineer</label>
              <Input
                id="supplier-support-quality-engineer"
                value={supportQualityEngineer}
                placeholder="Not assigned"
                disabled={!canEditSettings || managementSaving}
                onChange={(e) => setSupportQualityEngineer(e.target.value)}
                onBlur={() => { if (canEditSettings) commitSupportQualityEngineer(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              />
            </div>
          </div>
          {managementSaving && (<div className="flex items-center gap-2 text-sm text-muted-foreground mt-3"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>)}
        </CardContent>
      </Card>

      {/* CR-4365: KPI Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" />KPI Settings</CardTitle>
          <CardDescription>Switch a score off here to leave this supplier out of it at every site. To leave it out at one site only, use Include in on Customer Relationships.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { field: 'include_in_otif' as const, label: 'Include in OTIF', description: 'Include this supplier in On-Time In-Full calculations' },
              { field: 'include_in_ncr_ppm' as const, label: `Include in NCR/${qualityLabel}`, description: `Include this supplier in NCR and ${qualityLabel} calculations` },
              { field: 'include_in_otd' as const, label: 'Include in OTD', description: 'Include this supplier in On-Time Delivery calculations' },
            ].map(({ field, label, description }) => (
              <div key={field} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">{label}</label>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
                <Switch checked={supplier[field]} onCheckedChange={async (checked) => { if (canEditKpiSwitches) { try { await onUpdateProfile({ [field]: checked } as Partial<SupplierProfile>); toast.success(`${label} ${checked ? 'enabled' : 'disabled'}`); } catch (err: unknown) { toast.error(apiErrorMessage(err, `Failed to update ${label}`)); } } }} disabled={!canEditKpiSwitches} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CR-QBR: QBR Schedule linked to contract governance */}
      <QBRScheduleSection supplierId={supplier.company} />

      {!canEditSettings && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">You have view-only access to supplier settings. Contact your administrator for modifications.</p>
          </div>
        </div>
      )}

    </div>
  );
}
